import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'

import { ISmbNode } from '../model/types'

/** Category / sub-category rows use this key for their own "overall" settings row */
const OVERALL_CATEGORY = 'jbp-overall'

const isOverallKey = (value: string | null | undefined) => !value || value === OVERALL_CATEGORY

/**
 * Node identity, with the "overall" key and an empty key treated as the same thing - the
 * settings row and the stored split row do not always spell that level the same way.
 */
export const getSmbNodeId = (
  programType: string,
  category: string,
  subCategory: string | null | undefined,
  l3Category: string | null | undefined
) =>
  [
    programType,
    category,
    isOverallKey(subCategory) ? '' : subCategory,
    isOverallKey(l3Category) ? '' : l3Category,
  ].join('::')

interface IIndexedSetting {
  setting: IFormActualsTargetsSettings
  settingsIndex: number
}

/**
 * Builds the whole offering hierarchy the split is planned on. The offering itself has no
 * Financial Planning settings row, so it is synthesized above every non-summary category;
 * each category and its descendants still come straight from the planning settings and keep
 * their original settings indexes for financial-value lookup.
 */
/** Answers whether a category is switched off as Not applicable in Planning */
export type TIsSmbCategoryNotApplicable = (category: string, mandatoryType: number) => boolean

export const buildSmbTree = (
  formats: IFormActualsTargetsSettings[],
  programType: string,
  isCategoryNotApplicable?: TIsSmbCategoryNotApplicable,
  disabledNodeIds?: ReadonlySet<string>
): ISmbNode | null => {
  const isDisabledNode = (nodeId: string) => !!disabledNodeIds?.has(nodeId)

  const rows = formats.flatMap<IIndexedSetting>((setting, settingsIndex) =>
    setting.ProgramType === programType &&
    !setting.SummaryProgram &&
    setting.Category !== OVERALL_CATEGORY
      ? [{ setting, settingsIndex }]
      : []
  )

  if (!rows.length) {
    return null
  }

  const categoryKeys: string[] = []
  rows.forEach((row) => {
    const { Category } = row.setting
    if (!categoryKeys.includes(Category)) {
      categoryKeys.push(Category)
    }
  })

  const categoryOrderOf = (key: string) => {
    const row = rows.find((el) => el.setting.Category === key)

    return row ? row.setting.CategoryOrder : 0
  }
  categoryKeys.sort((a, b) => categoryOrderOf(a) - categoryOrderOf(b))

  const categoryNodes = categoryKeys.map<ISmbNode>((category) => {
    const categoryRows = rows.filter((row) => row.setting.Category === category)
    const categoryRow = categoryRows.find((row) => isOverallKey(row.setting.SubCategory))
    // Not applicable is a category-level switch, so the whole subtree inherits it
    const isNotApplicable = !!isCategoryNotApplicable?.(
      category,
      categoryRows[0].setting.MandatoryType
    )

    const subCategoryKeys: string[] = []
    categoryRows.forEach((row) => {
      const { SubCategory } = row.setting
      if (!isOverallKey(SubCategory) && !subCategoryKeys.includes(SubCategory)) {
        subCategoryKeys.push(SubCategory)
      }
    })

    const subCategoryOrderOf = (key: string) => {
      const row = categoryRows.find((el) => el.setting.SubCategory === key)

      return row ? row.setting.SubCategoryOrder : 0
    }
    subCategoryKeys.sort((a, b) => subCategoryOrderOf(a) - subCategoryOrderOf(b))

    const children = subCategoryKeys.map<ISmbNode>((subCategory) => {
      const subCategoryRows = categoryRows.filter((row) => row.setting.SubCategory === subCategory)
      const ownRow = subCategoryRows.find((row) => isOverallKey(row.setting.L3Category))

      const l3Nodes = subCategoryRows
        .filter((row) => !isOverallKey(row.setting.L3Category))
        .sort((a, b) => a.setting.L3CategoryOrder - b.setting.L3CategoryOrder)
        .map<ISmbNode>((row) => ({
          id: getSmbNodeId(programType, category, subCategory, row.setting.L3Category),
          kind: 'l3',
          nameKey: row.setting.L3Category,
          settingsIndex: row.settingsIndex,
          programType,
          category,
          subCategory,
          l3Category: row.setting.L3Category,
          isNotApplicable,
          isDisabled: isDisabledNode(
            getSmbNodeId(programType, category, subCategory, row.setting.L3Category)
          ),
          children: [],
        }))

      return {
        // Some planning configurations do not have a dedicated L3 "overall" settings row.
        // Keep the synthetic sub-category node separate from its first L3 child in that case;
        // reusing the first child used to create duplicate ids and corrupt expansion/storage.
        id: getSmbNodeId(programType, category, subCategory, ownRow?.setting.L3Category || ''),
        kind: 'subCategory',
        nameKey: subCategory,
        settingsIndex: ownRow?.settingsIndex ?? -1,
        programType,
        category,
        subCategory,
        l3Category: ownRow?.setting.L3Category || '',
        isNotApplicable,
        isDisabled: isDisabledNode(
          getSmbNodeId(programType, category, subCategory, ownRow?.setting.L3Category || '')
        ),
        children: l3Nodes,
      }
    })

    return {
      id: getSmbNodeId(
        programType,
        category,
        categoryRow ? categoryRow.setting.SubCategory : '',
        ''
      ),
      kind: 'category',
      nameKey: category,
      settingsIndex: categoryRow?.settingsIndex ?? -1,
      programType,
      category,
      subCategory: categoryRow?.setting.SubCategory || '',
      l3Category: '',
      isNotApplicable,
      isDisabled: isDisabledNode(
        getSmbNodeId(programType, category, categoryRow?.setting.SubCategory || '', '')
      ),
      children,
    }
  })

  return {
    id: getSmbNodeId(programType, OVERALL_CATEGORY, '', ''),
    kind: 'offering',
    nameKey: programType,
    settingsIndex: -1,
    programType,
    category: OVERALL_CATEGORY,
    subCategory: '',
    l3Category: '',
    isNotApplicable: false,
    isDisabled: isDisabledNode(getSmbNodeId(programType, OVERALL_CATEGORY, '', '')),
    children: categoryNodes,
  }
}

/** Flattens the tree, parents before children */
export const flattenSmbTree = (node: ISmbNode): ISmbNode[] => [
  node,
  ...node.children.flatMap(flattenSmbTree),
]

/** Ancestor chain of `nodeId`, outermost first; empty when the node is the root or unknown */
export const getSmbAncestors = (root: ISmbNode, nodeId: string): ISmbNode[] => {
  let result: ISmbNode[] | null = null

  const walk = (node: ISmbNode, chain: ISmbNode[]): boolean => {
    if (node.id === nodeId) {
      result = chain

      return true
    }

    return node.children.some((child) => walk(child, [...chain, node]))
  }

  walk(root, [])

  return result || []
}

export const findSmbNode = (root: ISmbNode, nodeId: string): ISmbNode | null => {
  if (root.id === nodeId) {
    return root
  }

  for (const child of root.children) {
    const found = findSmbNode(child, nodeId)
    if (found) {
      return found
    }
  }

  return null
}
